import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { ApplicationsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  ApplicationRowFragment,
  AtsHiringRolesEnum,
  BulkMoveToPhaseDocument,
  BulkMoveToPhaseMutation,
  GetApplicationPhasesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCandidateEmailActions } from 'modules/ats/hooks/candidateEmailActions/useCandidateEmailActions'
import { ApplicationPhase, useApplicationPhases } from 'modules/ats/hooks/useApplicationPhases'
import { sortApplicationPhases } from 'modules/ats/lib/utils/applicationPhase'

import { PhaseChangedSmartActionPreview } from './PhaseChangedSmartActionPreview'

interface Props {
  jobId: string
  applications: Pick<ApplicationRowFragment, 'id'>[] &
    {
      applicationPhase: Pick<ApplicationPhase, 'id'>
    }[]
  onClose: () => void
  onSuccess: () => void
  hiringRole: AtsHiringRolesEnum
}

export const MoveToPhaseModal = ({
  applications,
  jobId,
  onSuccess,
  onClose,
  hiringRole,
}: Props) => {
  const [selectedPhaseId, setSelectedPhaseId] = React.useState<string>()
  const [skipSmartAction, setSkipSmartAction] = React.useState<boolean>(false)
  const { loading, applicationPhases } = useApplicationPhases({
    variables: { atsJobPostingId: jobId },
  })
  const [bulkMoveToPhase, { loading: moving }] = useMutation(BulkMoveToPhaseDocument, {
    extractorFn: (data: BulkMoveToPhaseMutation) => data.atsMutations.bulkMoveToPhaseAtsApplication,
  })
  const { candidateEmailActions } = useCandidateEmailActions(
    {
      atsJobPostingId: jobId,
      enabled: true,
      actionType: 'phase_changed',
      atsApplicationPhaseIds: selectedPhaseId ? [selectedPhaseId] : [],
    },
    { skip: !selectedPhaseId }
  )
  const { featureAvailability } = useFeatureAvailability(Features.ats_smart_actions)
  const orderedApplicationPhases = sortApplicationPhases(applicationPhases)
  const currentApplicationPhaseId = applications[0]?.applicationPhase.id
  const { access, company } = useCurrentSession()
  const smartActionsActive = featureAvailability.available
  const smartActionTriggered = candidateEmailActions.length > 0 && !skipSmartAction

  const phaseOptions = orderedApplicationPhases.map((phase) => ({
    label: phase.name,
    value: phase.id,
    disabled: phase.id === currentApplicationPhaseId,
  }))
  const onSubmit = async () => {
    const selectedPhase = applicationPhases.find((phase) => phase.id === selectedPhaseId)

    if (!selectedPhase) return

    const response = await bulkMoveToPhase({
      variables: {
        ids: applications.map((application) => application.id),
        authorId: access.id,
        atsApplicationPhaseId: selectedPhase.id,
        skipSmartAction: skipSmartAction,
      },
      refetchQueries: [ApplicationsDataTableDocument, GetApplicationPhasesDocument],
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.candidates.applications.move_to_phase.modal.success_message'),
      })

      if (smartActionsActive && smartActionTriggered) {
        track(trackingEvents.ats.smartActionPerform, {
          company_id: company.id,
          stage_id: [selectedPhase.hiringStage?.id],
          stage_name: [selectedPhase.hiringStage?.name || 'Unassigned'],
          job_position: jobId,
          action_type: 'phase_changed',
        })
      }
      track(trackingEvents.ats.candidateMovedPhase, {
        hiring_role: hiringRole,
        new_phase_id: selectedPhase.id,
        old_phase_id: currentApplicationPhaseId,
        user_id: access.id,
      })

      onSuccess()
    }
  }

  return (
    <Modal
      illustration="add-user"
      title={i18n.tp('ats.candidates.applications.move_to_phase.modal.title', {
        count: applications.length,
      })}
      description={i18n.t('ats.candidates.applications.move_to_phase.modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!selectedPhaseId || moving} submit>
            {i18n.t('ats.candidates.applications.move_to_phase.modal.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <Select
          label={i18n.t('ats.candidates.applications.move_to_phase.modal.phase')}
          none={i18n.t('ats.candidates.applications.move_to_phase.modal.select_prompt')}
          options={phaseOptions}
          onChange={(phase) => setSelectedPhaseId(String(phase))}
          readonly={loading}
        />

        {smartActionsActive && candidateEmailActions[0]?.id && (
          <PhaseChangedSmartActionPreview
            onToggleAction={setSkipSmartAction}
            smartAction={candidateEmailActions[0]}
          />
        )}
      </Box>
    </Modal>
  )
}
